import React from 'react';

import { SaveCollectionEntryModal } from 'src/app/graph/shared/saveCollectionEntryModal/SaveCollectionEntryModal';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { GraphRef } from '../../../../hooks/useGraphRef';
import {
  CollectionEntryIdState,
  CollectionEntryState,
  SharedCollectionEntryState,
} from '../../../hooks/useExplorerState/useEditorTabState/collectionEntryState';
import {
  EditorTabState,
  useTabbedDisplayNames,
} from '../../../hooks/useExplorerState/useEditorTabState/useEditorTabState';
import {
  headersRemoteEntryFromArray,
  useHeadersManagerContext,
} from '../../../hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';

export const SaveCollectionEntryFromExplorerModal = ({
  graphRef,
  operationTabId,
  operationState,
  dispatchSavedOperationLocalStateAction,
  isOpen,
  onClose,
}: {
  graphRef: GraphRef | null;
  operationTabId: string;
  operationState: CollectionEntryState;
  dispatchSavedOperationLocalStateAction: EditorTabState['dispatchSavedOperationLocalStateAction'];
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { tabbedDisplayNames } = useTabbedDisplayNames();
  const trackCustomEvent = useTrackCustomEvent();
  const {
    tabbedHeaderDefinitions,
    setTabbedHeaderDefinitions,
    sharedHeaderDefinitions,
  } = useHeadersManagerContext();

  const onCollectionEntrySaved = React.useCallback(
    async ({
      collectionId,
      collectionEntryId,
      collectionType,
      sharedCollectionOriginVariantRef,
      sharedCollectionOriginVariantHeaders,
    }: {
      collectionType: 'sandbox' | 'shared' | 'personal';
    } & CollectionEntryIdState &
      SharedCollectionEntryState) => {
      dispatchSavedOperationLocalStateAction({
        type: 'SaveOperation',
        collectionId,
        collectionEntryId,
        collectionEntryState: operationState,
        operationTabId,
        isShared: collectionType === 'shared',
        sharedCollectionOriginVariantRef,
        sharedCollectionOriginVariantHeaders,
      });

      // inject headers from the origin variant when this
      // operation is saved as an operation in the variant
      const selectedSharedVariantSharedHeaders = headersRemoteEntryFromArray(
        sharedCollectionOriginVariantHeaders?.filter(
          (originSharedHeader) =>
            !sharedHeaderDefinitions.some(
              (currentVariantSharedHeader) =>
                currentVariantSharedHeader.headerName ===
                  originSharedHeader.headerName &&
                currentVariantSharedHeader.value === originSharedHeader.value,
            ),
        ) ?? [],
      );

      if (isMinLengthArray(1, selectedSharedVariantSharedHeaders)) {
        setTabbedHeaderDefinitions(
          tabbedHeaderDefinitions.concat(
            selectedSharedVariantSharedHeaders?.map((sharedHeader) => ({
              headerName: sharedHeader.name,
              value: sharedHeader.value,
              checked: true,
              key: Math.random(),
            })),
          ),
        );
      }
      trackCustomEvent({
        action: 'save_collection_entry',
        category: 'Operation Collections',
        label: collectionType,
      });
    },
    [
      dispatchSavedOperationLocalStateAction,
      operationState,
      operationTabId,
      setTabbedHeaderDefinitions,
      sharedHeaderDefinitions,
      tabbedHeaderDefinitions,
      trackCustomEvent,
    ],
  );

  return (
    <SaveCollectionEntryModal
      isOpen={isOpen}
      title="Save Operation"
      graphRef={graphRef}
      operationState={operationState}
      initialCollectionEntryName={tabbedDisplayNames[operationTabId] ?? ''}
      onCollectionEntrySaved={onCollectionEntrySaved}
      onClose={onClose}
    />
  );
};
